import { action, flow, makeObservable, observable } from 'mobx';
import { FormStore, GlobalStore } from '@roc/feature-app-core';
import { BankStatementsService } from '../services/bankStatementsService';
import { NewFileUpload } from '@roc/ui';
import { downloadDocument } from '@roc/feature-utils';

export const readHolders = (json: string): string[] => {
  try {
    const parsed = JSON.parse(json || '[]');
    return Array.isArray(parsed) ? parsed.filter(h => typeof h === 'string') : [];
  } catch {
    return [];
  }
};

export const formatHolders = (holders: string[]) =>
  (holders || [])
    .filter(h => typeof h === 'string')
    .map(h => h.trim())
    .filter(Boolean)
    .join('; ');

const parseHolders = (value: string): string[] =>
  (value || '')
    .split(';')
    .map(h => h.trim())
    .filter(Boolean);

const bankStatementForm = {
  fields: {
    accountHolders: { value: '', error: null, rule: 'required', message: 'Account holders are required' },
    accountLast4: { value: '', error: null, rule: 'required|digits:4', message: 'Account last 4 digits are required' },
    statementPeriodEnd: { value: '', error: null, rule: 'required', message: 'Statement period end is required' },
    endingBalance: { value: '', error: null, rule: 'required', message: 'Ending balance is required' },
    appliedToLiquidity: { value: false, error: null, rule: '' },
  },
  meta: { isValid: true, error: null },
};

const otherSourceForm = {
  fields: {
    amount: { value: '', error: null, rule: 'required', message: 'Amount is required' },
    comments: { value: '', error: null, rule: '' },
  },
  meta: { isValid: true, error: null },
};

export interface BankStatementOtherSource {
  bankStatementOtherSourceId?: number;
  loanId: number;
  borrowerId: number;
  amount: number;
  comments?: string;
  createdBy?: string;
  createdDate?: string;
}

export interface BankStatement {
  bankStatementRecordId: number;
  bankStatementDocumentId: number;
  accountLast4: string;
  accountHoldersJson: string;
  statementPeriodEnd: string;
  endingBalance: number;
  appliedToLiquidity: boolean;
  originalFileName: string;
  loanId: number;
  borrowerId: number;
  uploadedDate?: string;
}

export class BankStatementsStore {
  requiredAmount = 0;
  borrowerAppliedLiquidity = 0;
  totalAppliedLiquidity = 0;
  bankStatements: BankStatement[] = [];
  otherSources: BankStatementOtherSource[] = [];
  status: string = null;
  documents: NewFileUpload[] = [];
  editFormStore: FormStore;
  otherSourceFormStore: FormStore;
  statementPreviewData: any = null;

  multipleBorrowers = false;
  borrowerName: string = null;
  personalGuarantor = false;
  showBreakdown = false;

  private bankStatementsService: BankStatementsService;

  constructor(private globalStore: GlobalStore) {
    this.bankStatementsService = new BankStatementsService();
    this.editFormStore = new FormStore(bankStatementForm, globalStore);
    this.otherSourceFormStore = new FormStore(otherSourceForm, globalStore);
    makeObservable(this, {
      requiredAmount: observable,
      borrowerAppliedLiquidity: observable,
      totalAppliedLiquidity: observable,
      bankStatements: observable,
      otherSources: observable,
      status: observable,
      documents: observable,
      editFormStore: observable,
      otherSourceFormStore: observable,
      statementPreviewData: observable,

      multipleBorrowers: observable,
      borrowerName: observable,
      personalGuarantor: observable,
      showBreakdown: observable,
      setBankStatements: action,
      setDocuments: action,
      updateBankStatement: action,
      loadEditForm: action,
      loadOtherSourceForm: action,
      fetchBankStatements: flow,
      _persistRecord: flow,
      saveBankStatement: flow,
      saveOtherSource: flow,
      removeOtherSource: flow,
      uploadFiles: flow,
      downloadBankStatement: flow,
      previewBankStatement: flow
    });
  }

  setBankStatements(bankStatements: BankStatement[]) {
    this.bankStatements = bankStatements;
  }

  setDocuments(documents: NewFileUpload[]) {
    this.documents = documents;
  }

  updateBankStatement(updated: BankStatement) {
    this.bankStatements = this.bankStatements.map(s =>
      s.bankStatementRecordId === updated.bankStatementRecordId ? updated : s
    );
  }

  loadEditForm(statement: BankStatement) {
    this.editFormStore.loadForm({
      accountHolders: formatHolders(readHolders(statement.accountHoldersJson)),
      accountLast4: statement.accountLast4,
      statementPeriodEnd: statement.statementPeriodEnd,
      endingBalance: String(statement.endingBalance),
      appliedToLiquidity: statement.appliedToLiquidity,
    });
  }

  loadOtherSourceForm(record?: BankStatementOtherSource) {
    if (!record) {
      this.otherSourceFormStore.reset();
      return;
    }
    this.otherSourceFormStore.loadForm({
      amount: String(record.amount ?? ''),
      comments: record.comments ?? '',
    });
  }

  *fetchBankStatements(loanId: number, borrowerId: number) {
    try {
      const response = yield this.bankStatementsService.getBankStatementInfo(loanId, borrowerId);
      this.bankStatements = response?.data?.data?.bankStatements;
      this.otherSources = response?.data?.data?.otherSources ?? [];
      this.requiredAmount = response?.data?.data?.neededLiquidity;
      this.borrowerAppliedLiquidity = response?.data?.data?.borrowerAppliedLiquidity;
      this.totalAppliedLiquidity = response?.data?.data?.totalAppliedLiquidity;
      this.status = response?.data?.data?.status;
      this.multipleBorrowers = response?.data?.data?.multipleBorrowers ?? false;
      this.borrowerName = response?.data?.data?.borrowerName ?? null;
      this.personalGuarantor = response?.data?.data?.personalGuarantor ?? false;
      this.showBreakdown = response?.data?.data?.showBreakdown ?? false;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while fetching bank statements.',
      });
    }
  }

  *_persistRecord(record: BankStatement) {
    yield this.bankStatementsService.saveBankStatement(record);
    yield this.fetchBankStatements(record.loanId, record.borrowerId);
  }

  *saveBankStatement(id: number) {
    if (!this.editFormStore.runFormValidationWithMessage()) throw new Error();

    try {
      const existing = this.bankStatements.find(
        s => s.bankStatementRecordId === id
      );
      if (!existing) return;

      const { fields } = this.editFormStore.form;
      const holdersUnchanged =
        fields.accountHolders.value.trim() ===
        formatHolders(readHolders(existing.accountHoldersJson));
      const updated: BankStatement = {
        ...existing,
        ...this.editFormStore.getFormValues(),
        accountHoldersJson: holdersUnchanged
          ? existing.accountHoldersJson
          : JSON.stringify(parseHolders(fields.accountHolders.value)),
        endingBalance: Number(fields.endingBalance.value),
      };

      yield this._persistRecord(updated);
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Bank statement saved successfully.',
      });
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while saving the bank statement.',
      });
      throw new Error();
    }
  }

  *saveOtherSource(
    loanId: number,
    borrowerId: number,
    bankStatementOtherSourceId?: number
  ) {
    if (!this.otherSourceFormStore.runFormValidationWithMessage()) throw new Error();

    const { fields } = this.otherSourceFormStore.form;

    try {
      const record: BankStatementOtherSource = {
        bankStatementOtherSourceId,
        loanId,
        borrowerId,
        amount: Number(fields.amount.value),
        comments: String(fields.comments.value ?? '').trim(),
      };

      yield this.bankStatementsService.saveOtherSource(record);
      yield this.fetchBankStatements(loanId, borrowerId);
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Other source saved successfully.',
      });
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while saving the other source.',
      });
      throw new Error();
    }
  }

  *removeOtherSource(
    loanId: number,
    borrowerId: number,
    bankStatementOtherSourceId: number
  ) {
    try {
      yield this.bankStatementsService.deleteOtherSource(bankStatementOtherSourceId);
      yield this.fetchBankStatements(loanId, borrowerId);
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Other source removed successfully.',
      });
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while removing the other source.',
      });
      throw new Error();
    }
  }

  *uploadFiles(loanId: number, borrowerId: number) {
    if (this.documents.length === 0) {
      this.globalStore.notificationStore.showWarningNotification({
        message: 'Please upload at least one document before submitting.',
      });
      throw new Error();
    }

    try {
      yield this.bankStatementsService.uploadBankStatements(
        this.documents.map(d => d.file),
        loanId,
        borrowerId
      );
      this.documents = [];
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Bank statements uploaded successfully.',
      });
      yield this.fetchBankStatements(loanId, borrowerId);
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while uploading bank statements.',
      });
      throw new Error();
    }
  }

  *downloadBankStatement(id: number) {
    try {
      const statement = this.bankStatements.find(s => s.bankStatementRecordId === id);
      if (!statement) return;
      const response = yield this.bankStatementsService.downloadBankStatement(
        statement.bankStatementRecordId
      );
      downloadDocument(response?.data, response?.headers, 'download');
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while downloading the bank statement.',
      });
    }
  }

  *previewBankStatement(bankStatementRecordId: number) {
    try {
      this.statementPreviewData = null;
      const response = yield this.bankStatementsService.downloadBankStatement(
        bankStatementRecordId
      );
      this.statementPreviewData = response;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'An error occurred while loading the document preview.',
      });
    }
  }
}
